import {
  useCallback,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useDialogA11y } from "../lib/useDialogA11y";

/**
 * Shared confirm dialog — replaces window.confirm() / window.alert(), both of
 * which are broken in iOS standalone PWA mode (confirm returns immediately,
 * alert renders chromelessly with no app title). Wraps useDialogA11y so it
 * has focus trap + Escape-to-cancel + focus return.
 *
 * Imperative usage via the hook is intentional — it lets call sites replace
 * `if (!window.confirm(...)) return;` with `if (!await confirm({...})) return;`
 * one line for one line.
 *
 *   const { confirm, dialog } = useConfirmDialog();
 *   async function discard() {
 *     if (!await confirm({ title: "Discard?", confirmLabel: "Discard", danger: true })) return;
 *     ...
 *   }
 *   return <>{dialog}<button onClick={discard}>…</button></>;
 */

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export function useConfirmDialog() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({ title: "" });
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    setOpen(false);
    const r = resolverRef.current;
    resolverRef.current = null;
    if (r) r(result);
  }, []);

  const dialog = (
    <ConfirmDialog
      open={open}
      options={opts}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return { confirm, dialog };
}

interface ConfirmDialogProps {
  open: boolean;
  options: ConfirmOptions;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({ open, options, onConfirm, onCancel }: ConfirmDialogProps) {
  const titleId = useId();
  const { dialogRef } = useDialogA11y(open, onCancel);

  if (!open) return null;

  const {
    title,
    message,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    danger = false,
  } = options;

  return (
    <div style={styles.overlay} onClick={onCancel}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={styles.box}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} style={styles.title}>
          {title}
        </h2>
        {message && <div style={styles.message}>{message}</div>}
        <div style={styles.actions}>
          <button type="button" onClick={onCancel} style={styles.cancelBtn} autoFocus>
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={danger ? styles.dangerBtn : styles.confirmBtn}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 16,
  },
  box: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 24,
    width: "100%",
    maxWidth: 420,
    display: "flex",
    flexDirection: "column",
    gap: 14,
    boxShadow: "0 12px 48px rgba(0,0,0,0.6)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    margin: 0,
    color: "var(--text)",
    lineHeight: 1.25,
  },
  message: {
    fontSize: 14,
    color: "var(--muted)",
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 4,
    flexWrap: "wrap",
  },
  cancelBtn: {
    padding: "10px 16px",
    minHeight: 40,
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "transparent",
    color: "var(--muted)",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  confirmBtn: {
    padding: "10px 18px",
    minHeight: 40,
    borderRadius: 8,
    border: 0,
    background: "var(--gold)",
    color: "#0D0B08",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  dangerBtn: {
    padding: "10px 18px",
    minHeight: 40,
    borderRadius: 8,
    border: 0,
    background: "#E05040",
    color: "#fff",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
};
